import { useEffect, useState, useRef } from 'react';
import { useApp } from '../../../contexts/AppContext';
import { useNavigate } from 'react-router-dom';
import { useClerkAuth } from '../../../hooks/useClerkAuth';
import { Plus, Monitor } from 'lucide-react';
import { useToast } from '../../ui/Toast';
import Button from '../../ui/Button';
import { Card, CardContent, CardHeader } from '../../ui/Card';
import { AgentCardSkeleton } from '../../ui/SkeletonLoader';
import { NoAgentsEmpty, CanvaNotConnectedEmpty } from '../../ui/EmptyState';
import AgentCard from './AgentCard';
import LinkAgentDialog from './LinkAgentDialog';
import DownloadStationLink from './DownloadStationLink';
import BentoGrid, { BentoItem } from '../../layouts/BentoGrid';

/**
 * Agent List Component - Redesigned
 * Modern agent management with bento-grid layout
 */
export default function AgentList() {
  const { state, isApiReady, fetchLinkedAgents, fetchStationSettings, clearError } = useApp();
  const { linkedAgents, isAgentsLoading, error, stationSettings, stationSettingsStatus } = state;
  const { hasCanvaConnected } = useClerkAuth();
  const isCanvaConnected = hasCanvaConnected();
  const navigate = useNavigate();
  const toast = useToast();
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const hasFetchedRef = useRef(false);

  // Media Stations are only meaningful with Canva connected — don't fetch (or
  // render) them otherwise, so a disconnected account never sees stale stations
  // left over in AppContext from an earlier connected session.
  useEffect(() => {
    if (isApiReady && isCanvaConnected && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchLinkedAgents();
      // Each card shows and edits its station's ProPresenter library.
      fetchStationSettings();
    }
  }, [isApiReady, isCanvaConnected]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, toast, clearError]);

  const onlineAgents = linkedAgents.filter(agent => agent.status === 'online');
  const offlineAgents = linkedAgents.filter(agent => agent.status !== 'online');

  return (
    <>
      <div className="min-h-screen pb-4 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header Section */}
          <div className="mb-6">
            <h1 className="text-display text-gray-900 dark:text-gray-100 mb-2">
              Media Stations
            </h1>
            <p className="text-body text-gray-600 dark:text-gray-400">
              Add, remove and view the status of your media stations
            </p>
            <div className="mt-4">
              <DownloadStationLink testId="stations-download-link" />
            </div>
          </div>

          {/* Canva Not Connected — Media Stations require an active Canva connection */}
          {!isCanvaConnected && (
            <CanvaNotConnectedEmpty
              onAction={
                <Button
                  onClick={() => navigate('/settings?action=link-canva')}
                  variant="primary"
                  size="lg"
                >
                  Connect Canva Account
                </Button>
              }
            />
          )}

          {isCanvaConnected && (
          <>
          {/* Stats & Link Action Bento Grid */}
          <BentoGrid className="mb-6">
            <BentoItem size="large">
              <Card variant="glass" className="bg-gradient-to-br from-blue-500 to-purple-600 border-0 h-full">
                <CardContent className="text-white">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-heading-lg font-semibold mb-2">
                        Link New Media Station
                      </h2>
                      <p className="text-body-sm text-white/80">
                        Link a new Media Station to your account 
                      </p>
                    </div>
                    <Monitor className="w-12 h-12 text-white/50" />
                  </div>
                  
                  <Button
                    onClick={() => setShowLinkDialog(true)}
                    variant="secondary"
                    size="lg"
                    className="w-full sm:w-auto"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Link Media Station
                  </Button>
                </CardContent>
              </Card>
            </BentoItem>

            <BentoItem>
              <Card variant="elevated" className="h-full">
                <CardHeader>
                  <h3 className="text-heading font-semibold text-gray-900 dark:text-gray-100">
                    Active Media Stations
                  </h3>
                </CardHeader>
                <CardContent>
                  <div className="text-display text-emerald-600 dark:text-emerald-400">
                    {onlineAgents.length}
                  </div>
                  <p className="text-body-sm text-gray-600 dark:text-gray-400 mt-2">
                    Currently online
                  </p>
                </CardContent>
              </Card>
            </BentoItem>
          </BentoGrid>

          {/* Loading State */}
          {isAgentsLoading && linkedAgents.length === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => (
                <AgentCardSkeleton key={i} />
              ))}
            </div>
          )}

          {/* Empty State */}
          {!isAgentsLoading && linkedAgents.length === 0 && (
            <NoAgentsEmpty
              onAction={
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Button
                    onClick={() => setShowLinkDialog(true)}
                    variant="primary"
                    size="lg"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Link Your First Media Station
                  </Button>
                  <DownloadStationLink size="lg" testId="stations-empty-download-link" />
                </div>
              }
            />
          )}

          {/* Agents Grid */}
          {linkedAgents.length > 0 && (
            <div className="space-y-6">
              {/* Online Agents */}
              {onlineAgents.length > 0 && (
                <div>
                  <h3 className="text-heading font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse" />
                    Online
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {onlineAgents.map(agent => (
                      <AgentCard
                        key={agent.agent_id}
                        agent={agent}
                        libraryEntry={stationSettings[agent.agent_id]}
                        libraryStatus={stationSettingsStatus}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Offline Agents */}
              {offlineAgents.length > 0 && (
                <div>
                  <h3 className="text-heading font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-2" />
                    Offline
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {offlineAgents.map(agent => (
                      <AgentCard
                        key={agent.agent_id}
                        agent={agent}
                        libraryEntry={stationSettings[agent.agent_id]}
                        libraryStatus={stationSettingsStatus}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          </>
          )}
        </div>
      </div>

      <LinkAgentDialog
        isOpen={showLinkDialog}
        onClose={() => setShowLinkDialog(false)}
      />
    </>
  );
}
